import { Component, OnDestroy, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { OtpService } from '../../../../core/services/otp.service';
import { RegistrationService } from '../../../../core/services/registration.service';
import {
  CreateAccountPayload,
  IdentityDocType,
} from '../../../../core/models/registration.models';
import {
  formatBirthDateForApi,
  getFileExtension,
  IDENTITY_DOC_OPTIONS,
  PASSWORD_HINT,
  PASSWORD_PATTERN,
  readFileAsBase64,
} from '../../../../core/utils/registration.utils';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { getPhoneValidationError, resolvePhoneCredentials } from '../../../../shared/utils/phone.utils';

const TOTAL_STEPS = 6;

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnDestroy {
  @ViewChild('phoneInput') phoneInput?: PhoneInputComponent;

  readonly totalSteps = TOTAL_STEPS;
  readonly identityDocOptions = IDENTITY_DOC_OPTIONS;
  readonly passwordHint = PASSWORD_HINT;

  currentStep = 0;
  loading = false;
  hidePassword = true;
  hideConfirmPassword = true;
  errorMessage = '';
  successMessage = '';

  profilePhotoPreview: string | null = null;
  profilePhotoBase64: string | null = null;
  identityFileName = '';
  identityFileBase64: string | null = null;
  identityFileType = 'jpg';

  registrationPayload: CreateAccountPayload | null = null;
  otpPhoneDisplay = '';
  otpResendSeconds = 0;
  private otpTimerSub?: Subscription;

  readonly form = this.fb.group({
    phone: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.minLength(1)]],
    firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    lastName: [''],
    email: ['', [Validators.required, Validators.email]],
    address: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
    birthPlace: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
    birthDate: ['', [Validators.required, this.birthDateValidator.bind(this)]],
    niu: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{14}$/)]],
    password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
    confirmPassword: ['', [Validators.required]],
    docType: ['passport' as IdentityDocType, [Validators.required]],
    docNumber: ['', [Validators.required, Validators.minLength(3)]],
    docExpiry: ['', [Validators.required, Validators.pattern(/^\d{2}\/\d{2}\/\d{4}$/)]],
    bankAccount: [''],
    bankName: [''],
    bankCode: [''],
    otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly registrationService: RegistrationService,
    private readonly otpService: OtpService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {
    this.form.controls.confirmPassword.addValidators(
      this.passwordMatchValidator.bind(this)
    );
  }

  get stepLabels(): string[] {
    return ['Identité', 'Mot de passe', 'Photo', 'Pièce', 'Banque', 'OTP'];
  }

  getPhoneError(): string | null {
    const control = this.form.controls.phone;
    if (!control.touched || control.valid) {
      return null;
    }

    if (control.hasError('required')) {
      return 'Le téléphone est requis';
    }

    return getPhoneValidationError(control.errors?.['invalidPhone']);
  }

  ngOnDestroy(): void {
    this.otpTimerSub?.unsubscribe();
  }

  previousStep(): void {
    this.errorMessage = '';
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  async nextStep(): Promise<void> {
    this.errorMessage = '';

    if (this.currentStep === 5) {
      await this.completeRegistration();
      return;
    }

    const valid = await this.validateCurrentStep();
    if (!valid) {
      return;
    }

    if (this.currentStep === 4) {
      await this.sendOtpAndProceed();
      return;
    }

    this.currentStep++;
  }

  onProfilePhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Veuillez sélectionner une image.';
      return;
    }

    readFileAsBase64(file).then((base64) => {
      this.profilePhotoBase64 = base64;
      this.profilePhotoPreview = `data:${file.type};base64,${base64}`;
      this.errorMessage = '';
    });
  }

  onIdentityFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    readFileAsBase64(file).then((base64) => {
      this.identityFileBase64 = base64;
      this.identityFileName = file.name;
      this.identityFileType = getFileExtension(file);
      this.errorMessage = '';
    });
  }

  resendOtp(): void {
    if (this.otpResendSeconds > 0 || !this.registrationPayload) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.otpService
      .sendOtp(
        this.registrationPayload.username,
        this.registrationPayload.prefixCountry
      )
      .subscribe({
        next: (result) => {
          this.loading = false;
          if (result.status === 'SUCCESS') {
            this.startOtpTimer();
            return;
          }
          this.errorMessage = this.getOtpSendErrorMessage(result.status);
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Impossible de renvoyer le code OTP.';
        },
      });
  }

  private async validateCurrentStep(): Promise<boolean> {
    switch (this.currentStep) {
      case 0:
        return this.validatePersonalStep();
      case 1:
        return this.validateFormFields(['password', 'confirmPassword']);
      case 2:
        if (!this.profilePhotoBase64) {
          this.errorMessage = 'Veuillez attacher une photo de profil.';
          return false;
        }
        return true;
      case 3:
        if (!this.validateFormFields(['docType', 'docNumber', 'docExpiry'])) {
          return false;
        }
        if (!this.identityFileBase64) {
          this.errorMessage = 'Veuillez joindre une pièce d’identité.';
          return false;
        }
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  }

  private async validatePersonalStep(): Promise<boolean> {
    const fields = [
      'phone',
      'name',
      'firstName',
      'email',
      'address',
      'birthPlace',
      'birthDate',
      'niu',
    ] as const;

    if (!this.validateFormFields([...fields])) {
      return false;
    }

    const { phone, niu } = this.form.getRawValue();
    const { username, dialCode } = resolvePhoneCredentials(
      phone!,
      this.phoneInput?.getInstance()
    );

    this.loading = true;
    try {
      const result = await firstValueFrom(
        this.registrationService.checkAvailability(username, niu!)
      );
      if (!result.available) {
        this.errorMessage = result.message ?? 'Ce numéro ou ce NIU est déjà utilisé.';
        return false;
      }
      return true;
    } catch {
      this.errorMessage = 'Impossible de vérifier la disponibilité du compte.';
      return false;
    } finally {
      this.loading = false;
    }
  }

  private validateFormFields(fields: string[]): boolean {
    let valid = true;
    for (const field of fields) {
      const control = this.form.get(field);
      control?.markAsTouched();
      if (control?.invalid) {
        valid = false;
      }
    }
    return valid;
  }

  private async sendOtpAndProceed(): Promise<void> {
    if (!this.identityFileBase64) {
      this.errorMessage = 'Veuillez joindre une pièce d’identité.';
      return;
    }

    this.registrationPayload = this.buildPayload();
    this.otpPhoneDisplay = this.form.controls.phone.value ?? '';
    this.loading = true;

    try {
      const result = await firstValueFrom(
        this.otpService.sendOtp(
          this.registrationPayload.username,
          this.registrationPayload.prefixCountry
        )
      );

      if (result.status !== 'SUCCESS') {
        this.errorMessage = this.getOtpSendErrorMessage(result.status);
        return;
      }

      this.currentStep = 5;
      this.startOtpTimer();
    } catch {
      this.errorMessage = 'Impossible d’envoyer le code OTP. Réessayez plus tard.';
    } finally {
      this.loading = false;
    }
  }

  private async completeRegistration(): Promise<void> {
    const otpControl = this.form.controls.otp;
    otpControl.markAsTouched();
    if (otpControl.invalid || !this.registrationPayload) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const verifyResult = await firstValueFrom(
        this.otpService.verifyOtp(
          this.registrationPayload.username,
          otpControl.value!
        )
      );

      if (verifyResult.status !== 'OTP_OK') {
        this.errorMessage =
          verifyResult.status === 'OTP_ESPIRED'
            ? 'Le code OTP a expiré. Demandez un nouveau code.'
            : verifyResult.message ?? 'Code OTP invalide.';
        return;
      }

      const createResult = await firstValueFrom(
        this.registrationService.createAccount(this.registrationPayload)
      );

      if (createResult.status !== 'SUCCESS') {
        this.errorMessage = this.getCreateAccountErrorMessage(createResult.status);
        return;
      }

      const loginResult = await this.authService.login(
        this.registrationPayload.username,
        this.registrationPayload.password,
        false,
        this.registrationPayload.prefixCountry
      );

      if (loginResult.status !== 'SUCCESS') {
        this.successMessage = 'Compte créé. Connectez-vous avec vos identifiants.';
        this.router.navigate(['/se-connecter']);
        return;
      }

      this.router.navigate(['/profil']);
    } catch {
      this.errorMessage = 'Une erreur est survenue lors de la création du compte.';
    } finally {
      this.loading = false;
    }
  }

  private buildPayload(): CreateAccountPayload {
    const raw = this.form.getRawValue();
    const { username, dialCode } = resolvePhoneCredentials(
      raw.phone!,
      this.phoneInput?.getInstance()
    );
    const dateNaiss = formatBirthDateForApi(raw.birthDate!) ?? raw.birthDate!;

    const payload: CreateAccountPayload = {
      photoProfil: this.profilePhotoBase64!,
      username,
      name: raw.name!.trim(),
      firstName: raw.firstName!.trim(),
      lastName: raw.lastName?.trim() ?? '',
      niu: raw.niu!.toUpperCase().trim(),
      dateNaiss,
      lieuNaiss: raw.birthPlace!.trim(),
      address: raw.address!.trim(),
      email: raw.email!.trim(),
      password: raw.password!,
      enabled: true,
      totp: false,
      emailVerified: false,
      codepays: dialCode,
      prefixCountry: dialCode,
      identityDocuments: [
        {
          docBase64: [this.identityFileBase64!],
          numeroPiece: raw.docNumber!.trim(),
          typeDoc: raw.docType as IdentityDocType,
          dateExpiration: raw.docExpiry!.trim(),
          typeFile: this.identityFileType,
        },
      ],
    };

    if (raw.bankAccount?.trim() && raw.bankName?.trim() && raw.bankCode?.trim()) {
      payload.bank_account_number = raw.bankAccount.trim();
      payload.bank_name = raw.bankName.trim();
      payload.bank_code = raw.bankCode.trim();
    }

    return payload;
  }

  private startOtpTimer(): void {
    this.otpTimerSub?.unsubscribe();
    this.otpResendSeconds = 60;
    this.otpTimerSub = new Subscription();

    const intervalId = window.setInterval(() => {
      this.otpResendSeconds--;
      if (this.otpResendSeconds <= 0) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    this.otpTimerSub.add(() => window.clearInterval(intervalId));
  }

  private birthDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value as string)?.trim();
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return { birthDateFormat: true };
    }

    const [, day, month, year] = match.map(Number);
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return { birthDateInvalid: true };
    }

    const today = new Date();
    if (date > today) {
      return { birthDateFuture: true };
    }

    const age =
      today.getFullYear() -
      year -
      (today.getMonth() < month - 1 ||
      (today.getMonth() === month - 1 && today.getDate() < day)
        ? 1
        : 0);

    if (age < 18) {
      return { birthDateMinor: true };
    }
    if (age > 120) {
      return { birthDateTooOld: true };
    }

    return null;
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = this.form?.controls.password.value;
    if (!control.value || control.value === password) {
      return null;
    }
    return { passwordMismatch: true };
  }

  private getOtpSendErrorMessage(status: string): string {
    switch (status) {
      case 'OFF_SERVICE':
      case 'OTP_NOT_SAVE':
        return 'Service SMS indisponible pour l’instant. Réessayez plus tard.';
      default:
        return 'Impossible d’envoyer le code OTP.';
    }
  }

  private getCreateAccountErrorMessage(status?: string): string {
    switch (status) {
      case 'EMAIL_EXIST':
        return 'Cette adresse e-mail est déjà utilisée.';
      case 'USERNAME_EXIST':
        return 'Ce numéro de téléphone est déjà utilisé.';
      default:
        return 'La création du compte a échoué. Réessayez plus tard.';
    }
  }
}
